import axios from "../../api/axiosInstance";
import { STATUS, getProjectId, extractErrorInfo } from "./sharedfunction";
import { TRIAL_FEATURE_MODEL_KEYS } from "../../constants/trialLimits";
import { getModelDisplayName } from "../../enums/AIModelEnums";

/**
 * Single catalog row for trial orgs when GET /ai-feature-model is not used (plan trial).
 * Matches backend trial resolution (OpenAI + TRIAL_FEATURE_MODEL_KEYS).
 *
 * @param {string} featureKey - e.g. `test_launch`, `ai_generator`, `automation_recording`
 * @returns {{ provider: string, model_key: string, display_name: string, is_recommended: boolean, label: string } | null}
 */
export function buildTrialSyntheticModelForFeature(featureKey) {
  const model_key = featureKey ? TRIAL_FEATURE_MODEL_KEYS[featureKey] : null;
  if (!model_key) return null;
  const provider = "openai";
  const display_name = getModelDisplayName(provider, model_key);
  return {
    provider,
    model_key,
    display_name,
    is_recommended: true,
    label: "Default",
  };
}

function buildModelLabel(baseName, { isDefault = false, isRecommended = false } = {}) {
  return `${baseName}${isDefault ? " (default)" : ""}${isRecommended ? " (recommended)" : ""}`;
}

/**
 * Flattens GET /ai-feature-model feature rows into a deduplicated model list (provider + model_key).
 * Recommended models sort first. Exported for unit tests.
 *
 * Do not use this for feature-specific UIs (dropdowns, defaults): `is_recommended` and `label` are
 * per-feature; the first occurrence wins and can show the wrong "(default)" for another feature.
 *
 * @param {Array<{ available_models?: Array<{ provider: string, model_key: string, display_name?: string, is_recommended?: boolean }> }>} features
 * @returns {Array<{ provider: string, model_key: string, display_name?: string, is_recommended?: boolean }>}
 */
export function flattenFeatureMappingsToUniqueModels(features) {
  const seen = new Set();
  const models = [];
  (features || []).forEach((f) => {
    (f.available_models || []).forEach((m) => {
      const key = `${m.provider}:${m.model_key}`;
      if (!seen.has(key)) {
        seen.add(key);
        models.push(m);
      }
    });
  });
  models.sort(
    (a, b) => Number(!!b.is_recommended) - Number(!!a.is_recommended)
  );
  return models;
}

/**
 * Add a new AI Model
 * POST /aimodel/add
 * @param {Object} params - Model parameters
 * @param {string} params.name - Display name for the model
 * @param {string} params.modelname - Model name identifier
 * @param {string} params.modeltype - Model type/version
 * @param {string} params.apiKey - API key for the model (will be encrypted)
 * @param {string} [params.status] - Model status (Active/Inactive)
 * @returns {Object} Response object with status and data
 */
export async function addAIModel({ name, modelname, modeltype, apiKey, status = "Active", is_default } = {}) {
  try {
    if (!name || name.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Model name is required",
      };
    }

    if (!modelname || modelname.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Model name identifier is required",
      };
    }

    if (!modeltype || modeltype.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Model type is required",
      };
    }

    if (!apiKey || apiKey.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "API key is required",
      };
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/aimodel/add`,
      { 
        data: {
          name,
          modelname,
          modeltype,
          apiKey,
          status,
          ...(typeof is_default === "boolean" ? { is_default } : {})
        }
      },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error adding AI model:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get all AI Models for the organization
 * GET /aimodel/getall
 * @returns {Object} Response object with status and data array
 */
export async function getAllAIModels() {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/aimodel/getall`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching AI models:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Update an existing AI Model
 * PUT /aimodel/update/:id
 * @param {Object} params - Update parameters
 * @param {string} params.id - Model ID
 * @param {string} params.name - Display name for the model
 * @param {string} params.modelname - Model name identifier
 * @param {string} params.modeltype - Model type/version
 * @param {string} [params.apiKey] - API key for the model (optional)
 * @param {string} [params.status] - Model status
 * @returns {Object} Response object with status and data
 */
export async function updateAIModel({ id, name, modelname, modeltype, apiKey, status, is_default } = {}) {
  try {
    if (!id || id.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Model ID is required",
      };
    }

    if (!name || name.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Model name is required",
      };
    }

    if (!modelname || modelname.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Model name identifier is required",
      };
    }

    if (!modeltype || modeltype.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Model type is required",
      };
    }

    const updateData = {
      name,
      modelname,
      modeltype,
      status
    };

    if (typeof is_default === "boolean") {
      updateData.is_default = is_default;
    }

    // Only include apiKey if provided
    if (apiKey && apiKey.trim().length > 0) {
      updateData.apiKey = apiKey;
    }

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/aimodel/update/${id}`,
      { data: updateData },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating AI model:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Delete one or more AI Models
 * DELETE /aimodel/delete
 * @param {Object} params - Delete parameters
 * @param {Array<string>} params.ids - Array of model IDs to delete
 * @returns {Object} Response object with status and data
 */
export async function deleteAIModels({ ids } = {}) {
  try {
    if (!ids || !Array.isArray(ids) || ids.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "At least one model ID is required",
      };
    }

    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/aimodel/delete`,
      {
        data: { ids },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error deleting AI models:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get active AI Models for the organization — backed by org feature→model mappings + catalog.
 * GET /ai-feature-model
 *
 * @param {string|null} [featureKey] - When set (e.g. `test_launch`, `ai_generator`), returns only that
 *   feature's `available_models` so `is_recommended` / `label` match that section. When omitted, returns a
 *   legacy deduped union (avoid for new UI).
 * @param {Object} [options]
 * @param {boolean} [options.trialOrgSkip]
 * @param {string|number|null} [options.projectId] - Pass from project-scoped (automation) screens so the
 *   permission middleware evaluates the caller's project-scoped roles. Omit on org-level screens.
 * @returns {Object} Response object with status and data array ({ provider, model_key, display_name, is_recommended, label? }[])
 */
export async function getActiveAIModels(featureKey = null, options = {}) {
  if (options.trialOrgSkip) {
    if (featureKey) {
      const synthetic = buildTrialSyntheticModelForFeature(featureKey);
      return {
        status: STATUS.SUCCESS,
        data: synthetic ? [synthetic] : [],
      };
    }
    return {
      status: STATUS.SUCCESS,
      data: [],
    };
  }
  try {
    const params = new URLSearchParams();
    if (options.projectId != null && options.projectId !== "") {
      params.set("projectId", String(options.projectId));
    }
    const qs = params.toString();
    const url = `${process.env.REACT_APP_API_URL}/ai-feature-model${qs ? `?${qs}` : ""}`;

    const response = await axios.get(url, {
      withCredentials: true,
    });

    if (response?.data?.success) {
      const rows = response?.data?.data || [];
      let models;
      if (featureKey) {
        const row = rows.find((r) => r.feature_key === featureKey);
        const current = row?.current_model;
        models = (row?.available_models || []).map((m) => {
          const isDefault =
            !!current &&
            current.provider === m.provider &&
            current.model_key === m.model_key;
          const baseName = m.display_name || m.model_key;
          return {
            ...m,
            is_default: isDefault,
            label: buildModelLabel(baseName, {
              isDefault,
              isRecommended: Boolean(m.is_recommended),
            }),
          };
        });
      } else {
        models = flattenFeatureMappingsToUniqueModels(rows);
      }
      return {
        status: STATUS.SUCCESS,
        data: models,
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching active AI models:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Automation-compatible model list for the org (catalog filtered by connected providers).
 * Same underlying data as {@link getActiveAIModels}. `section` is ignored (org-wide catalog), but
 * `projectId` is forwarded so the permission middleware can evaluate project-scoped roles — pass it
 * from project-scoped (automation) screens.
 * @param {{ projectId?: string|number|null, section?: string }} [params]
 * @returns {Object} Response object with status and data array
 */
export async function fetchAutomationModels(params = {}) {
  return getActiveAIModels(null, { projectId: params.projectId });
}

/**
 * Get model details with decrypted API key
 * GET /aimodel/:projectId/fetch-model-details/:id
 * @param {Object} params - Parameters
 * @param {string} params.id - Model ID
 * @param {string} [params.projectId] - Project ID (optional, will use current project if not provided)
 * @param {string} [params.section] - 'script_creator' | 'test_launch' | 'script_manager' for trial default model
 * @returns {Object} Response object with status and data
 */
export async function fetchModelDetails({ id, projectId, section } = {}) {
  try {
    if (!id || id.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Model ID is required",
      };
    }

    const resolvedProjectId = projectId ?? getProjectId();
    const params = new URLSearchParams();
    if (resolvedProjectId) params.set('projectId', resolvedProjectId);
    if (section) params.set('section', section);
    const query = params.toString();
    const url = `${process.env.REACT_APP_API_URL}/aimodel/fetch-model-details/${id}${query ? `?${query}` : ''}`;

    const response = await axios.get(
      url,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching model details:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Set the default AI model for the organization
 * PATCH /aimodel/set-default/:id
 * @param {string} id - Model ID
 * @returns {Object} Response object with status and data
 */
export async function setDefaultModel(id) {
  try {
    if (!id || id.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Model ID is required",
      };
    }

    const response = await axios.patch(
      `${process.env.REACT_APP_API_URL}/aimodel/set-default/${id}`,
      {},
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error setting default AI model:", error);
    return extractErrorInfo(error);
  }
}

